import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

// ─────────────────────────────────────────────────────────────────
// Prototype C — Visual Header Navigation
//
// Replaces text-only navigation items with compact photographic
// navigation items, on the existing horizontal black header. The
// rest of the page below (imported Landing) renders unchanged.
//
// Constraints observed:
//  • Black header background, wordmark position, language switcher,
//    navigation order, existing routes, horizontal desktop
//    composition — all preserved.
//  • Header height kept at 96px (existing h-24) by sizing the
//    thumbnail + label stack to fit within the current row.
//  • Gold active state preserved via existing token.
//  • RTL rendered natively when ?lang=ar is present.
//  • No mobile redesign in this prototype (per brief) — the mobile
//    drawer keeps the current text-only pattern.
// ─────────────────────────────────────────────────────────────────

// Working label set for the prototype. Provided by the user and
// clearly identified as temporary evaluation captions — not
// editorially approved and not locked.
const VISUAL_NAV = [
    {
        to: "/",
        labelEn: "HOME",
        labelAr: "الرئيسية",
        // Existing homepage hero lineup — unmistakable arrival image.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/o8nteh4a_IMG_6144.JPG",
        end: true,
    },
    {
        to: "/about",
        labelEn: "CrewZNatioN",
        labelAr: "CrewZNatioN",
        // Community — "Inside the meet — Riyadh comes together"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/u65gd357_IMG-20260503-WA0051.jpg",
        // Institutional English asset — retained in both language versions
        protectedEn: true,
    },
    {
        to: "/gathering",
        labelEn: "The Gathering",
        labelAr: "The Gathering",
        // Iconic "Not Tokyo — This is Riyadh"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/tf8y44s5_F3209F41-0B30-4C13-8807-7FCBDFB18ABD.PNG",
        protectedEn: true,
    },
    {
        to: "/events",
        labelEn: "EVENTS",
        labelAr: "الفعاليات",
        // Corvette trio under the festival lights — active meet
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/l3h9h8v0_35B1CA2D-6A44-433F-A0BB-19EE971F256E.JPEG",
    },
    {
        to: "/gallery",
        labelEn: "MOMENTS",
        labelAr: "اللحظات",
        // "1958 in full bloom — purple lowrider with roses"
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/eyed6v8w_E4BE464A-17CA-400C-9126-D68CE352C4E2.JPG",
    },
    {
        to: "/sponsors",
        labelEn: "PARTNERS",
        labelAr: "الشراكات",
        // No authentic partner-activation image in archive.
        image: null,
    },
    {
        to: "/store",
        labelEn: "CrewZNatioN GEAR",
        labelAr: "CrewZNatioN GEAR",
        // No authentic gear / merch image in archive.
        image: null,
        protectedEn: true,
    },
    {
        to: "/contact",
        labelEn: "WELCOME",
        labelAr: "حياك",
        // Liberty Walk under the festival lights — people alongside a
        // machine, warm string-light night. Provisional stand-in
        // pending a dedicated welcoming social moment photograph.
        image: "https://customer-assets.emergentagent.com/job_crew-connect-68/artifacts/3b3z9inq_IMG_3024.JPEG",
    },
];

// A protected English institutional token rendered LTR-isolated when
// the label sits in the Arabic (RTL) header, matching the pattern
// established in NavbarAr (§34.2).
function renderLabel(item, isAr) {
    const label = isAr ? item.labelAr : item.labelEn;
    if (item.protectedEn && isAr) {
        // "The Gathering" gets gold on "Gathering" to match the
        // existing Navbar treatment.
        if (item.labelEn === "The Gathering") {
            return (
                <span dir="ltr" className="font-body inline-block">
                    The <span className="text-[var(--crewz-gold)]">Gathering</span>
                </span>
            );
        }
        return (
            <span dir="ltr" className="font-body inline-block">
                {item.labelEn}
            </span>
        );
    }
    // Gold on "Gathering" in English too, matching Navbar behavior.
    if (item.labelEn === "The Gathering" && !isAr) {
        return (
            <>
                The <span className="text-[var(--crewz-gold)]">Gathering</span>
            </>
        );
    }
    return label;
}

// A single image-led nav item. Structure:
//   [ thumbnail image ]
//   [ compact label   ]
// The complete stack is the clickable link. Falls back to a marked
// placeholder tile when no authentic image is available.
function VisualNavItem({ item, isAr }) {
    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
                `group flex flex-col items-center gap-1.5 transition-opacity duration-300 ${
                    isActive ? "opacity-100" : "opacity-90 hover:opacity-100"
                }`
            }
            data-testid={`visual-nav-${item.labelEn.toLowerCase().replace(/\s|&/g, "-")}`}
        >
            {({ isActive }) => (
                <>
                    <div
                        className={`relative w-[72px] h-[42px] overflow-hidden bg-[#1a1a1a] ring-1 transition-all duration-300 ${
                            isActive
                                ? "ring-[var(--crewz-gold)]"
                                : "ring-white/10 group-hover:ring-white/25"
                        }`}
                    >
                        {item.image ? (
                            <img
                                src={item.image}
                                alt=""
                                aria-hidden="true"
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                                draggable={false}
                            />
                        ) : (
                            // Awaiting authentic imagery — clearly marked
                            // internal placeholder. Not a stock image, not a
                            // generic icon, not an illustration.
                            <div
                                className="absolute inset-0 flex items-center justify-center bg-[#171717] border border-dashed border-white/15"
                                aria-hidden="true"
                            >
                                <span className="text-[9px] tracking-[0.18em] uppercase text-white/35">
                                    photo
                                </span>
                            </div>
                        )}
                        {/* Slight brightness lift on hover */}
                        <div
                            className="absolute inset-0 bg-black/25 group-hover:bg-black/5 transition-colors duration-500"
                            aria-hidden="true"
                        />
                        {/* Bottom fade for label edge if this element ever
                            carried an inline label — kept for visual
                            consistency with the rest of the site. */}
                    </div>
                    <span
                        className={`${
                            isAr
                                ? item.protectedEn
                                    ? "font-body uppercase tracking-[0.14em] text-[10px] font-bold"
                                    : "font-arabic tracking-normal text-[12px] font-bold"
                                : "font-body uppercase tracking-[0.14em] text-[10px] font-bold"
                        } leading-none whitespace-nowrap ${
                            isActive ? "text-[var(--crewz-gold)]" : "text-zinc-200 group-hover:text-white"
                        } transition-colors duration-300`}
                        dir={isAr && item.protectedEn ? "ltr" : undefined}
                    >
                        {renderLabel(item, isAr)}
                    </span>
                </>
            )}
        </NavLink>
    );
}

// The visual header itself — same shell as Navbar / NavbarAr, with
// text nav items replaced by image-led items. Language switcher and
// wordmark preserved in existing positions.
function VisualHeaderNav({ isAr, toggleTo }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-50 bg-[#111111]/90 backdrop-blur-xl border-b border-white/5"
            data-testid="site-navbar-visual"
            dir={isAr ? "rtl" : "ltr"}
        >
            <div className="px-6 md:px-10 lg:px-16 h-24 flex items-center justify-between">
                {/* Image-led nav — visible on lg+, one horizontal row */}
                <nav
                    className={`hidden lg:flex flex-1 items-center justify-between gap-2 order-2 ${
                        isAr ? "lg:ml-6 xl:ml-8" : "lg:mr-6 xl:mr-8"
                    }`}
                    aria-label="Primary visual navigation"
                >
                    {VISUAL_NAV.map((item) => (
                        <VisualNavItem key={item.to} item={item} isAr={isAr} />
                    ))}
                    {/* Language switcher — text only, existing behavior */}
                    <Link
                        to={toggleTo}
                        className={
                            isAr
                                ? "font-body uppercase tracking-[0.2em] px-2 py-2 text-[12px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                                : "font-arabic normal-case tracking-normal px-2 py-2 text-[14px] font-bold text-zinc-200 hover:text-white transition-colors duration-300"
                        }
                        dir={isAr ? "ltr" : "rtl"}
                        data-testid="visual-nav-lang-switcher"
                    >
                        {isAr ? "English" : "العربية"}
                    </Link>
                </nav>

                {/* Wordmark — existing position and size preserved */}
                <Link
                    to={isAr ? "/prototypes/visual-header?lang=ar" : "/prototypes/visual-header"}
                    className="flex items-center gap-3 order-1 lg:order-4"
                    data-testid="visual-nav-logo"
                >
                    <img
                        src={LOGO_URL}
                        alt="CrewZNatioN"
                        className="h-14 md:h-16 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                {/* Mobile toggle — mobile navigation is intentionally
                    unchanged in this prototype per brief. */}
                <button
                    className="lg:hidden text-white p-2 order-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="visual-nav-mobile-toggle"
                    aria-label={isAr ? "فتح القائمة" : "Toggle menu"}
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {/* Mobile drawer — text-only, unchanged from existing site
                behavior (mobile is not part of this prototype scope). */}
            {open && (
                <div
                    className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl"
                    data-testid="visual-nav-mobile-menu"
                >
                    <div className={`px-6 py-6 flex flex-col gap-1 ${isAr ? "font-arabic" : "font-body"}`}>
                        {VISUAL_NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-xs tracking-[0.18em] border-b border-white/5 ${
                                        isActive ? "text-[var(--crewz-gold)]" : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderLabel(item, isAr)}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}

// The prototype page. Renders the replacement header, then the
// existing Landing homepage below it, unchanged. Because Landing
// mounts its own Navbar, we deliberately re-import the Landing
// body-only equivalent: instead, we mount Landing directly with its
// own navbar hidden via a CSS scope so the visual header is the
// only header shown.
import Landing from "@/pages/Landing";

export default function VisualHeaderPrototype() {
    const location = useLocation();
    const isAr = location.search.includes("lang=ar");
    const toggleTo = isAr
        ? "/prototypes/visual-header"
        : "/prototypes/visual-header?lang=ar";

    return (
        <div
            className="visual-header-prototype min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]"
            data-testid="visual-header-prototype"
            dir={isAr ? "rtl" : "ltr"}
        >
            <VisualHeaderNav isAr={isAr} toggleTo={toggleTo} />

            {/* Existing Homepage rendered unchanged below the header.
                The Landing component mounts its own <Navbar/> at the
                top; we hide that instance via a local CSS scope so the
                visual header is the only header on this route. This
                keeps the Landing component itself untouched. */}
            <div className="visual-header-body">
                <Landing />
            </div>

            {/* Scoped rule — cannot escape this route. Hides the
                Landing-embedded [data-testid="site-navbar"] only when
                it renders inside this prototype. The live /
                homepage is not affected. */}
            <style>{`
                .visual-header-prototype .visual-header-body [data-testid="site-navbar"] {
                    display: none !important;
                }
            `}</style>
        </div>
    );
}
